'use strict';

/**
 * Open-ended vs yes/no cash card. Figures only from persisted forecast view.
 */
import { reconcileForecastView } from '../cashReport/reconcileForecastView.js'

function isYesNoCashQuestion(question) {
  const t = String(question || '').toLowerCase();
  if (/\bam i short\b/.test(t)) return true;
  if (/\bwill i be covered\b/.test(t)) return true;
  if (/\bare we short\b/.test(t)) return true;
  if (/\bam i covered\b/.test(t)) return true;
  return false;
}

function isOpenEndedCashQuestion(question) {
  const t = String(question || '').toLowerCase();
  if (isYesNoCashQuestion(t)) return false;
  return /\b(coming in|cash coming|cash flow|cash[- ]positive|pending this week|expected in|more cash)\b/.test(t);
}

function isCashCardQuestion(question) {
  return isYesNoCashQuestion(question) || isOpenEndedCashQuestion(question);
}

function moneyText(m) {
  if (m && typeof m.formatted === 'string' && m.formatted) return m.formatted;
  if (m && m.amount != null) {
    const n = Number(m.amount);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n < 0 ? `-$${abs}` : `$${abs}`;
  }
  return '—';
}

function weekOutAmount(week) {
  if (!week) return 0;
  const keys = ['subs_out', 'other_out', 'job_costs_out', 'payroll', 'overhead'];
  return keys.reduce((s, k) => s + (Number(week[k] && week[k].amount) || 0), 0);
}

function shortProjectName(name) {
  let n = String(name || 'Project').trim();
  n = n.replace(/^Taj Campton SF\s+/i, '').replace(/^Taj Campton\s*[-–]\s*/i, '');
  if (/\bcommon area\b/i.test(n)) return 'Common Area';
  return n || 'Project';
}

function formatCollectGroup(group) {
  const nums = group.numbers.filter((x) => x != null && x !== '');
  const hash = nums.length ? nums.map((n) => `#${n}`).join(' and ') : 'pay apps';
  let owner = group.owner_name ? String(group.owner_name).replace(/\s+SF$/i, '').trim() : '';
  owner = (owner.split(/\s+/)[0] || owner || 'the owner');
  return `Collect ${shortProjectName(group.project_name)} ${hash} — ${moneyText(group.amount)}, past ${owner}'s usual window`;
}

function lateCollectGroups(version) {
  const payload = (version && version.payload) || {};
  const now = Array.isArray(payload.collect_now) ? payload.collect_now : [];
  const owners = new Map();
  for (const row of now) {
    if (!row.owner_name) continue;
    if (row.project_id != null) owners.set(String(row.project_id), row.owner_name);
    if (row.project_name) owners.set(String(row.project_name), row.owner_name);
  }
  const fromCollections = ((payload.collections && payload.collections.items) || [])
    .filter((r) => r.status === 'late');
  const raw = fromCollections.length ? fromCollections : now;
  const by = new Map();
  for (const row of raw) {
    const pid = row.project_id != null ? String(row.project_id) : String(row.project_name || 'x');
    const amt = row.amount && row.amount.amount != null ? row.amount : { amount: Number(row.amount) || 0 };
    const dollars = Number(amt.amount) || 0;
    const owner = row.owner_name || owners.get(pid) || owners.get(String(row.project_name || ''));
    const existing = by.get(pid) || {
      project_name: row.project_name,
      owner_name: owner,
      numbers: [],
      amount: 0,
    };
    existing.owner_name = existing.owner_name || owner;
    existing.numbers.push(row.pay_app_number != null ? row.pay_app_number : null);
    existing.amount += dollars;
    by.set(pid, existing);
  }
  return [...by.values()]
    .map((g) => ({
      ...g,
      amount: { amount: g.amount, formatted: moneyText({ amount: g.amount }) },
    }))
    .sort((a, b) => (Number(b.amount.amount) || 0) - (Number(a.amount.amount) || 0));
}

function helpLines(version, actions) {
  const lines = [];
  const late = lateCollectGroups(version);
  for (const g of late) lines.push(formatCollectGroup(g));
  const fromArg = actions && Array.isArray(actions.top) ? actions.top : null;
  const fromPayload = version && version.payload && version.payload.actions && version.payload.actions.top;
  const top = (fromArg && fromArg.length ? fromArg : null)
    || (Array.isArray(fromPayload) && fromPayload.length ? fromPayload : [])
    || [];
  const reasons = (version && version.confidence_reasons) || [];
  const ranked = top.length
    ? top
    : reasons.map((r) => ({ card_kind: r.code, title: r.label }));
  for (const card of ranked) {
    if (card.card_kind === 'late_receivable' && late.length) continue;
    if (card.card_kind === 'bank_stale') {
      lines.push("Confirm today's bank balance");
      continue;
    }
    if (card.title) lines.push(String(card.title));
  }
  return lines.slice(0, 3);
}

function openCashCardModel(version, actions) {
  const payload = (version && version.payload) || {};
  const view = reconcileForecastView(version);
  const collections = payload.collections || {};
  const items = Array.isArray(collections.items) ? collections.items : [];
  const expected = { amount: view.expectedIn, formatted: moneyText({ amount: view.expectedIn }) };
  const n = items.length;
  const payLabel = n === 1 ? 'pay app' : 'pay apps';
  const lead = `${moneyText(expected)} expected in over the next 30 days from ${n} ${payLabel}.`;
  const due = (payload.payments_list && payload.payments_list.total)
    || { amount: view.dueOut };
  const week0 = (payload.weeks && payload.weeks[0]) || {};
  const lowestWeek = (payload.weeks || []).find((w) => {
    const close = w.close && Number(w.close.amount);
    return close === view.lowest;
  })?.week_start || (payload.verdict && payload.verdict.lowest_week) || null;
  return {
    lead,
    expected: moneyText(expected),
    payAppCount: n,
    dueOut: moneyText(due),
    week0Close: moneyText(week0.close),
    lowest: moneyText({ amount: view.lowest }),
    lowestWeek,
    confidence: String((version && version.confidence) || 'unknown'),
    help: helpLines(version, actions || payload.actions),
  };
}

export {
  isYesNoCashQuestion,
  isOpenEndedCashQuestion,
  isCashCardQuestion,
  moneyText,
  weekOutAmount,
  openCashCardModel,
  helpLines,
};
